import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

/**
 * TWO separate polite live regions (research.md R-10).
 *
 * Save acknowledgements are frequent; time milestones are rare and important.
 * Sharing one region would let a save announcement clobber a time warning
 * mid-utterance, which is exactly the failure that makes a timed test unusable
 * with a screen reader. Neither region ever takes focus.
 */
interface Announcer { saveState: (msg: string) => void; timeMilestone: (msg: string) => void }

const Ctx = createContext<Announcer>({ saveState: () => {}, timeMilestone: () => {} });
export const useAnnounce = () => useContext(Ctx);

export function AnnouncerProvider({ children }: { children: React.ReactNode }) {
  const [save, setSave] = useState('');
  const [time, setTime] = useState('');
  const lastTime = useRef('');

  const value = useMemo<Announcer>(() => ({
    saveState: (msg) => setSave(msg),
    timeMilestone: (msg) => {
      if (lastTime.current === msg) return; // announce each milestone once
      lastTime.current = msg;
      setTime(msg);
    },
  }), []);

  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">{save}</div>
      <div className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">{time}</div>
    </Ctx.Provider>
  );
}
